import { cn } from '@tempo-io/sui-class-utils';
import * as React from 'react';
import type { SettleTrackableEntry } from '@/common/queries/itemsTreeSetSettleState';
import { useTreeSetSettleState } from '@/common/queries/useTreeSetSettleState';

const EMPTY_ENTRIES: readonly SettleTrackableEntry[] = [];

/** Shared with the CSS selectors in `ItemsTable.tsx`. */
export const SETTLE_PENDING_CLASS = 'items-table-row-settle-pending';
export const SETTLE_SETTLED_CLASS = 'items-table-row-settle-settled';
export const SETTLE_RETAINED_CLASS = 'items-table-row-settle-retained';

/**
 * Row-mark class name (`items-table-row-settle-*`) for one table's own tree-set entries. `!enabled`
 * skips the settle-state subscription entirely and always returns `''`.
 *
 * Takes just `{ id }`, not the full `GridRowClassNameParams` — narrower here is still assignable
 * to DataGridPro's `getRowClassName` prop.
 */
export const useSettleRowClassName = (
  entries: readonly SettleTrackableEntry[] | undefined,
  enabled: boolean,
): ((params: { id: string | number }) => string) => {
  const { pendingIds, settledIds, retainedIds } = useTreeSetSettleState(
    entries ?? EMPTY_ENTRIES,
    enabled,
  );
  return React.useCallback(
    (params: { id: string | number }) => {
      if (!enabled) return '';
      const id = Number(params.id);
      return cn(
        pendingIds.has(id) && SETTLE_PENDING_CLASS,
        settledIds.has(id) && SETTLE_SETTLED_CLASS,
        retainedIds.has(id) && SETTLE_RETAINED_CLASS,
      );
    },
    [enabled, pendingIds, settledIds, retainedIds],
  );
};
